import { HStack, VStack } from '@chakra-ui/react';
import { Button, Tag } from 'Atoms';
import {
  FETCH_BATCH_SIZE,
  TARGET_INITIAL_COUNT,
  TARGET_LOAD_MORE_COUNT,
  useEsrsAuditLogs,
} from 'Features/History/History.hooks';
import {
  HistorySideBarFiltering,
  HistorySidebar,
  HistorySidebarHeader,
} from 'Features/History/HistorySidebar';
import { Drawer } from 'Molecules';
import { filterOptions } from 'Molecules/HistorySidebarFilter';
import { Typography } from 'Tokens';
import { RemoveIcon } from 'Tokens/Icons/Function';
import { uniq } from 'lodash';
import { EsrsAssessmentQuery_, QuestionType_Enum_ } from 'models';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'utils/translation';

export const EsrsAssessmentHistory = ({
  esrsAssessment,
  isHistorySidebarOpen,
  setIsHistorySidebarOpen,
}: {
  esrsAssessment: EsrsAssessmentQuery_['esrsAssessment'];
  isHistorySidebarOpen: boolean;
  setIsHistorySidebarOpen: (open: boolean) => void;
}) => {
  const { t: t1 } = useTranslation('audit');
  const { t: t2 } = useTranslation('files');
  const [searchValue, setSearchValue] = useState('');
  const shouldRenderContent = !!esrsAssessment && isHistorySidebarOpen;
  const [selected, setSelected] = useState<{ [x: string]: string[] }>({});
  const [auditLogsPagination, setAuditLogsPagination] = useState({
    limit: 0,
    offset: 0,
  });
  const [targetCount, setTargetCount] = useState(TARGET_INITIAL_COUNT);

  const {
    auditLogs,
    context,
    loading: auditLogsLoading,
    refetch: refetchAuditLogs,
    hasMoreData,
    refetchLoading,
  } = useEsrsAuditLogs({ esrsAssessment, ...auditLogsPagination, skip: !shouldRenderContent });

  const selectedStandards = selected['Standard'] ?? [];
  const isStandardSelected = (stdTitle: string) =>
    selectedStandards.length === 0 || selectedStandards.includes(stdTitle);

  const filterOptions: filterOptions = useMemo(
    () => ({
      Standard: context?.materialStandards?.map((std) => std.standard.title).sort() ?? [],
      'Disclosure requirement': uniq(
        context?.materialStandards
          ?.filter((std) => isStandardSelected(std.standard.title))
          .flatMap((std) => std.materialMetrics.map((met) => met.metric.requirement.title)) ?? []
      ).sort(),
      'Business unit': uniq(context?.reportingUnits?.map((ru) => ru.name) ?? []),
      'Datapoint owner': uniq(
        context?.reportingUnits?.flatMap((ru) =>
          ru.answers
            .filter((ans) =>
              context?.materialStandards
                ?.filter((std) => isStandardSelected(std.standard.title))
                .some((std) =>
                  std.materialMetrics.some((mm) => mm.metric.reference === ans.metricRef)
                )
            )
            .flatMap((ans) => ans.datapoints.map((dp) => dp.owner?.displayName ?? ''))
        ) ?? []
      )
        .filter(Boolean)
        .sort(),
      'Metric type': uniq(
        auditLogs
          .filter(
            (log) =>
              (selectedStandards.length === 0 ||
                selectedStandards.some((std) => log.metaInfo.standardName?.includes(std))) &&
              (!selected['Disclosure requirement'] ||
                selected['Disclosure requirement'].some((dr) => log.metaInfo.drName?.includes(dr)))
          )
          .map((log) => {
            const type = log.metaInfo.metricType?.toLowerCase();
            if (type === QuestionType_Enum_.Decimal_.toLowerCase()) return 'Decimal';
            if (type === QuestionType_Enum_.YesNo_.toLowerCase()) return 'Boolean';
            return 'Narrative';
          }) ?? []
      ),
    }),
    [context, auditLogs, selectedStandards, selected, isStandardSelected]
  );

  const loadMore = () => {
    setAuditLogsPagination((prev) => ({
      limit: FETCH_BATCH_SIZE,
      offset: prev.offset + prev.limit,
    }));
  };

  const incrementTargetCount = () => {
    setTargetCount((prev) => prev + TARGET_LOAD_MORE_COUNT);
  };

  useEffect(() => {
    if (isHistorySidebarOpen) {
      setTargetCount(TARGET_INITIAL_COUNT);
      setAuditLogsPagination({ limit: TARGET_INITIAL_COUNT, offset: 0 });
    }
  }, [isHistorySidebarOpen]);

  const handleRemoveFilter = (label: string, option: string) => {
    const currentOptions = selected[label] || [];
    const isSelected = currentOptions.includes(option);
    const updatedOptions = isSelected
      ? currentOptions.filter((opt) => opt !== option)
      : [...currentOptions, option];
    const updatedSelected = {
      ...selected,
      ...(updatedOptions.length > 0 ? { [label]: updatedOptions } : {}),
    };

    if (updatedOptions.length === 0) {
      delete updatedSelected[label];
    }
    setSelected(updatedSelected);
  };

  return (
    <Drawer
      onClose={() => {
        setIsHistorySidebarOpen(false);
      }}
      isOpen={shouldRenderContent}
      width="400px"
      hasOverlay={false}
      returnFocusOnClose={false}
      header={
        shouldRenderContent ? (
          <VStack width="100%" paddingBottom="0px" alignItems="start">
            <HistorySidebarHeader t={t1} />
            <HistorySideBarFiltering
              title={String(esrsAssessment?.reportingYear) ?? ''}
              refetchAuditLogs={refetchAuditLogs}
              searchValue={searchValue}
              setSearchValue={setSearchValue}
              filterOptions={filterOptions}
              selected={selected}
              setSelected={setSelected}
              t={t2}
            />
            {!!Object.values(selected).length && (
              <HStack wrap="wrap" gap="8px" w="100%">
                <Typography variant="bodyStrong" width="fit-content">
                  Filters:
                </Typography>
                {Object.values(selected).map((label) =>
                  label.map((option) => (
                    <Tag
                      title={option}
                      size="sm"
                      truncatable
                      rightIcon={<RemoveIcon color="text.muted" />}
                      onRemove={() =>
                        handleRemoveFilter(
                          Object.keys(selected).find((key) => selected[key] === label) ?? '',
                          option
                        )
                      }
                      maxW="48%"
                    />
                  ))
                )}
                {(Object.values(selected).length >= 2 ||
                  Object.values(selected).some((val) => val.length >= 2)) && (
                  <Button
                    size="sm"
                    variant="ghost"
                    leftIcon={<RemoveIcon color="text.muted" />}
                    onClick={() => setSelected({})}
                  >
                    <Typography variant="bodyStrong" color="text.muted">
                      Clear all
                    </Typography>
                  </Button>
                )}
              </HStack>
            )}
          </VStack>
        ) : undefined
      }
      headerProps={{
        paddingBottom: '0px',
        borderLeft: '1px solid',
        borderColor: 'border.decorative',
      }}
      bodyProps={{
        padding: '0px',
        borderLeft: '1px solid',
        borderColor: 'border.decorative',
      }}
    >
      {shouldRenderContent && (
        <HistorySidebar
          key="history-drawer"
          auditLogs={auditLogs}
          loading={auditLogsLoading}
          loadMore={loadMore}
          searchValue={searchValue}
          hasMoreData={hasMoreData}
          refetchLoading={refetchLoading}
          targetCount={targetCount}
          incrementTargetCount={incrementTargetCount}
          selected={selected}
        />
      )}
      <></>
    </Drawer>
  );
};
